import React, { useState } from 'react';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorStep, setTwoFactorStep] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorRecoveryCode, setTwoFactorRecoveryCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      const result = await login(email, password);
      if (result.success) {
        toast.success('Login successful!');
      } else if (result.requires2fa) {
        setTwoFactorStep(true);
        toast((t) => (
          <div>
            <p className="font-semibold">Two-factor authentication required</p>
            <p className="text-sm text-gray-300">Enter the 6-digit code from your authenticator.</p>
          </div>
        ));
      } else {
        setErrorMessage(result.error || 'Invalid credentials');
        toast.error(result.error || 'Invalid credentials');
      }
    } catch (error) {
      setErrorMessage('An error occurred. Please try again.');
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    const code = twoFactorCode.trim();

    if (!code || code.length !== 6) {
      setErrorMessage('Enter a valid 6-digit code');
      setLoading(false);
      return;
    }

    try {
      const result = await login(email, password, code, twoFactorRecoveryCode.trim() || '');
      if (result.success) {
        toast.success('Login successful!');
      } else {
        if (result.error) {
          setErrorMessage(result.error);
          toast.error(result.error);
        } else {
          setErrorMessage('Invalid 2FA code');
          toast.error('Invalid 2FA code');
        }
      }
    } catch (error) {
      setErrorMessage('An error occurred. Please try again.');
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Welcome to Arbitro</h1>
            <p className="text-gray-400">Professional Crypto Arbitrage Platform</p>
          </div>

          {!twoFactorStep ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field pl-10"
                    placeholder="admin@arbitro.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pl-10 pr-10"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {errorMessage && (
                <div className="text-sm text-red-400">{errorMessage}</div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3 flex items-center justify-center"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleTwoFactorSubmit} className="space-y-4">
              <div className="text-left">
                <p className="text-white font-semibold">Two-Factor Authentication</p>
                <p className="text-sm text-gray-400">Enter the 6-digit code from your authenticator app.</p>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">6-digit code</label>
                  <input
                    type="text"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value)}
                    className="input-field"
                    placeholder="123456"
                    maxLength={6}
                    required
                  />
                </div>
                <div>
                  <button
                    type="button"
                    onClick={() => setShowRecovery((prev) => !prev)}
                    className="text-xs text-blue-400 hover:text-blue-300"
                  >
                    {showRecovery ? 'Hide recovery code' : 'Use recovery code instead'}
                  </button>
                  {showRecovery && (
                    <input
                      type="text"
                      value={twoFactorRecoveryCode}
                      onChange={(e) => setTwoFactorRecoveryCode(e.target.value)}
                      className="input-field mt-2"
                      placeholder="Enter recovery code"
                    />
                  )}
                </div>
              </div>
              {errorMessage && (
                <div className="text-sm text-red-400">{errorMessage}</div>
              )}

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setTwoFactorStep(false);
                    setTwoFactorCode('');
                    setTwoFactorRecoveryCode('');
                    setErrorMessage('');
                  }}
                  className="w-1/2 py-2 rounded-md bg-gray-700 text-white hover:bg-gray-600"
                  disabled={loading}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-1/2 btn-primary py-2 flex items-center justify-center"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    'Verify & Sign In'
                  )}
                </button>
              </div>
            </form>
          )}

          <div className="mt-6 text-center text-sm text-gray-500">
            Contact your administrator to create an account
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
